import type { LngLat } from '@yandex/ymaps3-types'
import type { ClusterFeature } from '@/modules/YandexMap3Module/map-cluster.types'

interface ClusterObject<T = any> {
    world: Vector2
    lnglat: LngLat
    clusterId: string
    features: ClusterFeature<T>[]
}

interface ClusterMethod {
    render(params: { map: any; features: ClusterFeature<any>[] }): ClusterObject[]
}

interface Vector2 {
    x: number
    y: number
}

const WORLD_PIXEL_SIZE = 256

function divv(a: Vector2, b: Vector2, dst: Vector2 = { x: 0, y: 0 }): Vector2 {
    dst.x = a.x / b.x;
    dst.y = a.y / b.y;
    return dst;
}

function divn(v: Vector2, n: number, dst: Vector2 = { x: 0, y: 0 }): Vector2 {
    dst.x = v.x / n
    dst.y = v.y / n
    return dst
}

function convertPixelSizeToWorldSize(pixel: Vector2, zoom: number, dst?: Vector2): Vector2 {
    const scale = (2 ** zoom / 2) * WORLD_PIXEL_SIZE
    return divv(pixel, { x: scale, y: -scale }, dst ?? { x: 0, y: 0 })
}

function hashString(str: string): string {
    let hash = 2166136261
    for (let i = 0; i < str.length; i++) {
        hash ^= str.charCodeAt(i)
        hash *= 16777619
    }
    return (hash >>> 0).toString(36)
}

class ClusterByGridMethod implements ClusterMethod {
    private _gridSize: number
    private _featureIdCharCache: Record<string, string> = {}
    private _nextFeatureIndex = 0

    constructor(gridSize: number) {
        this._gridSize = gridSize
    }

    private _getClusterSizeWorld(targetZoom: number): number {
        return convertPixelSizeToWorldSize({ x: this._gridSize, y: 0 }, targetZoom).x
    }

    private _computeVisibleClusters(size: Vector2, targetZoom: number, center: Vector2): Map<string, boolean> {
        const halfViewportSize = divn(convertPixelSizeToWorldSize(size, targetZoom), 2)
        const top = center.y + halfViewportSize.y
        const bottom = center.y - halfViewportSize.y
        const left = center.x - halfViewportSize.x
        const right = center.x + halfViewportSize.x
        const clusterSize = this._getClusterSizeWorld(targetZoom)

        const minBucketX = Math.floor(left / clusterSize)
        const maxBucketX = Math.ceil(right / clusterSize)
        const minBucketY = Math.floor(top / clusterSize)
        const maxBucketY = Math.ceil(bottom / clusterSize)

        const result = new Map<string, boolean>()
        for (let x = minBucketX; x <= maxBucketX; x++) {
            for (let y = minBucketY; y <= maxBucketY; y++) {
                result.set(`${x}-${y}`, true)
            }
        }
        return result;
    }

    private _clusterize(map: any, features: ClusterFeature<any>[], targetZoom: number): Map<string, any> {
        const clusters = new Map<string, any>()
        const clusterSize = this._getClusterSizeWorld(targetZoom)

        for (const feature of features) {
            const object = {
                world: map.projection.toWorldCoordinates(feature.geometry.coordinates),
                lnglat: feature.geometry.coordinates,
                clusterId: '',
                features: [feature]
            };

            const clusterX = Math.floor(object.world.x / clusterSize)
            const clusterY = Math.floor(object.world.y / clusterSize)
            const id = `${clusterX}-${clusterY}`

            let cluster = clusters.get(id)
            if (!cluster) {
                cluster = { sumX: 0, sumY: 0, objects: [], features: [] }
                clusters.set(id, cluster)
            }

            cluster.sumX += object.world.x
            cluster.sumY += object.world.y
            cluster.objects.push(object)
            cluster.features.push(object.features[0])
        }

        return clusters
    }

    private _generateClusterId(features: ClusterFeature<any>[]): string {
        const result = ['cluster-']

        features.forEach(({ id }) => {
            if (!this._featureIdCharCache[id]) {
                this._featureIdCharCache[id] = String.fromCharCode(this._nextFeatureIndex)
                this._nextFeatureIndex += 1
            }
            result.push(this._featureIdCharCache[id]);
        });

        return result.join('')
    }

    render({ map, features }: { map: any; features: ClusterFeature<any>[] }): ClusterObject[] {
        const targetZoom = Math.round(map.zoom)
        const visibleClusters = this._computeVisibleClusters(map.size, targetZoom, map.projection.toWorldCoordinates(map.center))
        const currentCollection = this._clusterize(map, features, targetZoom)
        const nextViewportObjects: ClusterObject[] = []

        for (const [clusterId, cluster] of currentCollection.entries()) {
            if (!visibleClusters.get(clusterId)) continue

            const length = cluster.objects.length
            if (length === 1) {
                nextViewportObjects.push({
                    ...cluster.objects[0],
                    clusterId: cluster.features[0].id
                })
            } else {
                const world = { x: cluster.sumX / length, y: cluster.sumY / length }
                nextViewportObjects.push({
                    world,
                    lnglat: map.projection.fromWorldCoordinates(world),
                    clusterId: this._generateClusterId(cluster.features),
                    features: cluster.features
                })
            }
        }

        return nextViewportObjects
    }
}

export function clusterByGrid({ gridSize }: { gridSize: number }): ClusterMethod {
    return new ClusterByGridMethod(gridSize)
}